/**
 * @file PhotoTypeSlotItem Molecule Component
 * @description 사진 타입 슬롯 아이템 컴포넌트 (드래그 가능)
 * @see docs/plans/loading/05-implementation.md - Section 36
 */

import React, { memo, useCallback, useState } from 'react';
import styled from 'styled-components';
import { useSortable } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { Input } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import DraggableHandle from '@/src/components/atom/DraggableHandle';
import BasicButton from '@/src/components/atom/Button/BasicButton';
import Icon from '@/src/components/atom/Icon';
import ErrorCircleSvg from '@/src/assets/icons/icon-error-circle.svg';
import PhotoTypeSelect from '@/src/components/molecule/PhotoTypeSelect';
import colorSet from '@/src/styles/color';
import typo from '@/src/styles/typography';
import type { PhotoTypeCode } from '@/src/store/apis/photoType/interface';
import { ReactComponent as DoubleRightIcon } from '@/src/assets/icons/DoubleRight.svg';
import { ReactComponent as ChevronDownIcon } from '@/src/assets/icons/icon-chevron-down.svg';
import { ReactComponent as ChevronUpIcon } from '@/src/assets/icons/icon-chevron-up.svg';
import { ReactComponent as DeleteIcon } from '@/src/assets/icons/icon-delete.svg';

/**
 * 슬롯 데이터 인터페이스
 */
export interface PhotoTypeSlotData {
  /** 고유 ID (드래그용) */
  id: string;
  /** 사진 타입 코드 */
  typeCode: PhotoTypeCode;
  /** 제목 */
  title: string;
  /** 부제목 */
  subTitle: string | null;
  /** 표시 순서 */
  displayOrder: number;
  /** 활성화 여부 */
  isActive?: boolean;
}

export interface PhotoTypeSlotItemProps {
  slot: PhotoTypeSlotData;
  index: number;
  onTypeChange: (index: number, typeCode: PhotoTypeCode) => void;
  onTitleChange: (index: number, title: string) => void;
  onSubtitleChange: (index: number, subtitle: string) => void;
  onDelete?: (index: number) => void;
  onValidate?: (index: number, field: 'typeCode' | 'title' | 'subTitle', newValue?: string) => void;
  canDelete?: boolean;
  typeError?: string | null;
  titleError?: string | null;
  subtitleError?: string | null;
  disabled?: boolean;
  autoFocus?: boolean;
}

const SlotWrapper = styled.div<{ $isDragging?: boolean }>`
  position: ${({ $isDragging }) => ($isDragging ? 'relative' : 'static')};
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-radius: 8px;
  padding: 12px 8px 12px 4px;
  background: ${({ $isDragging }) => ($isDragging ? colorSet.gray11 : 'transparent')};
  opacity: ${({ $isDragging }) => ($isDragging ? 0.95 : 1)};
  box-shadow: ${({ $isDragging }) => ($isDragging ? '0px 4px 20px 0px rgba(0, 0, 0, 0.16)' : 'none')};
  z-index: ${({ $isDragging }) => ($isDragging ? 1 : 'auto')};
  transition: background 0.2s ease;

  &:hover {
    background: ${({ $isDragging }) => ($isDragging ? colorSet.gray11 : '#f7f9fa')};
  }
`;

const SlotContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ArrowIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 40px;
  flex-shrink: 0;
`;

const StyledDoubleRightIcon = styled(DoubleRightIcon)`
  width: 16px;
  height: 16px;
`;

const TitleWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 32px;
`;

const TitleInput = styled(Input)`
  flex: 1;
  ${typo.b7r}

  &.ant-input-affix-wrapper {
    border: 1px solid ${colorSet.gray9};
    border-radius: 8px;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05);
    padding: 8px 16px;

    &:hover {
      border-color: ${colorSet.gray7};
    }

    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: ${colorSet.blue4};
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05), 0 0 0 2px rgba(22, 109, 215, 0.1);
    }

    &.ant-input-status-error:not(.ant-input-disabled) {
      border-color: ${colorSet.red2} !important;

      &:hover,
      &:focus,
      &.ant-input-affix-wrapper-focused {
        border-color: ${colorSet.red2} !important;
      }
    }
  }

  .ant-input-suffix {
    color: ${colorSet.gray8};
    cursor: pointer;

    &:hover {
      color: ${colorSet.gray6};
    }
  }
`;

const SubtitleRow = styled.div`
  display: flex;
  padding-left: 48px;
  padding-right: 96px;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SubtitleInput = styled(Input)`
  flex: 1;
  ${typo.b7r}
  border: 1px solid ${colorSet.gray9};
  border-radius: 8px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05);
  padding: 8px 16px;

  &:hover {
    border-color: ${colorSet.gray7};
  }

  &:focus {
    border-color: ${colorSet.blue4};
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05), 0 0 0 2px rgba(22, 109, 215, 0.1);
  }

  &.ant-input-status-error:not(.ant-input-disabled) {
    border-color: ${colorSet.red2} !important;

    &:hover,
    &:focus {
      border-color: ${colorSet.red2} !important;
    }
  }
`;

const ErrorRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 48px;
  padding-right: 96px;
`;

const ErrorItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ErrorText = styled.span`
  ${typo.b9r}
  color: ${colorSet.red2};
`;

const IconButton = styled(BasicButton)`
  width: 40px;
  height: 40px;
`;

/**
 * 아이콘 스타일 - 20x20, #222729 색상
 */
const StyledChevronDown = styled(ChevronDownIcon)`
  width: 20px;
  height: 20px;
  path {
    fill: #222729;
  }
`;

const StyledChevronUp = styled(ChevronUpIcon)`
  width: 20px;
  height: 20px;
  path {
    fill: #222729;
  }
`;

const StyledDeleteIcon = styled(DeleteIcon)`
  width: 20px;
  height: 20px;
  path {
    fill: #222729;
  }
`;

/**
 * PhotoTypeSlotItem - 사진 타입 슬롯 아이템
 *
 * @description
 * 드래그 가능한 사진 타입 슬롯 아이템입니다.
 * Figma 디자인에 따라 드래그 핸들, 타입 선택, 화살표, 제목 입력(태그형), 펼침/접힘 버튼, 삭제 버튼으로 구성됩니다.
 *
 * @example
 * ```tsx
 * <PhotoTypeSlotItem
 *   slot={slot}
 *   index={0}
 *   onTypeChange={handleTypeChange}
 *   onTitleChange={handleTitleChange}
 *   onSubtitleChange={handleSubtitleChange}
 *   onDelete={handleDelete}
 *   canDelete={true}
 *   titleError="사진 유형명을 입력해주세요"
 * />
 * ```
 */
const PhotoTypeSlotItemComponent: React.FC<PhotoTypeSlotItemProps> = ({
  slot,
  index,
  onTypeChange,
  onTitleChange,
  onSubtitleChange,
  onDelete,
  onValidate,
  canDelete = false,
  typeError,
  titleError,
  subtitleError,
  disabled = false,
  autoFocus = false,
}) => {
  const { t } = useTranslation(['photoType', 'common']);
  const [isExpanded, setIsExpanded] = useState(!!slot.subTitle);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: slot.id,
    disabled,
  });

  const style = {
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    transition,
  };

  // 핸들러 최적화
  const handleTypeChange = useCallback(
    (value: PhotoTypeCode) => {
      onTypeChange(index, value);
      onValidate?.(index, 'typeCode', value);
    },
    [index, onTypeChange, onValidate]
  );

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      onTitleChange(index, newValue);
      onValidate?.(index, 'title', newValue);
    },
    [index, onTitleChange, onValidate]
  );

  const handleTitleClear = useCallback(() => {
    onTitleChange(index, '');
    onValidate?.(index, 'title', '');
  }, [index, onTitleChange, onValidate]);

  const handleTitleBlur = useCallback(() => {
    onValidate?.(index, 'title');
  }, [index, onValidate]);

  const handleSubtitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      onSubtitleChange(index, newValue);
      onValidate?.(index, 'subTitle', newValue);
    },
    [index, onSubtitleChange, onValidate]
  );

  const handleSubtitleBlur = useCallback(() => {
    onValidate?.(index, 'subTitle');
  }, [index, onValidate]);

  const handleDelete = useCallback(() => {
    onDelete?.(index);
  }, [index, onDelete]);

  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // 에러가 있는지 확인
  const hasError = !!typeError || !!titleError || !!subtitleError;

  return (
    <SlotWrapper ref={setNodeRef} style={style} $isDragging={isDragging}>
      <SlotContainer>
        {/* 드래그 핸들 */}
        <DraggableHandle
          attributes={attributes}
          listeners={listeners}
          disabled={disabled}
          isDragging={isDragging}
        />

        {/* 타입 선택 */}
        <PhotoTypeSelect
          value={slot.typeCode}
          onChange={handleTypeChange}
          disabled={disabled}
          hasError={!!typeError}
        />

        {/* 화살표 아이콘 */}
        <ArrowIcon><StyledDoubleRightIcon /></ArrowIcon>

        {/* 제목 입력 */}
        <TitleWrapper>
          <TitleInput
            value={slot.title}
            onChange={handleTitleChange}
            onBlur={handleTitleBlur}
            placeholder={t('photoType:titlePlaceholder')}
            maxLength={20}
            disabled={disabled}
            autoFocus={autoFocus}
            status={titleError ? 'error' : undefined}
            suffix={
              <CloseCircleFilled
                style={{
                  fontSize: 20,
                  color: colorSet.gray8,
                  visibility: slot.title && !disabled ? 'visible' : 'hidden',
                }}
                onClick={slot.title && !disabled ? handleTitleClear : undefined}
              />
            }
          />
        </TitleWrapper>

        {/* 버튼 그룹 - 펼침/삭제 버튼 */}
        <ButtonGroup>
          {/* 펼침/접힘 버튼 */}
          <IconButton
            buttonGrade="tertiary"
            buttonColor="black"
            buttonSize={40}
            isIconOnly
            onClick={toggleExpand}
            disabled={disabled}
            aria-label={isExpanded ? t('common:collapse') : t('common:expand')}
          >
            {isExpanded ? <StyledChevronUp /> : <StyledChevronDown />}
          </IconButton>

          {/* 삭제 버튼 */}
          <IconButton
            buttonGrade="tertiary"
            buttonColor="black"
            buttonSize={40}
            isIconOnly
            onClick={handleDelete}
            disabled={!canDelete || disabled}
            aria-label={t('common:delete')}
          >
            <StyledDeleteIcon />
          </IconButton>
        </ButtonGroup>
      </SlotContainer>

      {/* 부제목 영역 (펼침 시에만 표시) */}
      {isExpanded && (
        <SubtitleRow>
          <SubtitleInput
            value={slot.subTitle || ''}
            onChange={handleSubtitleChange}
            onBlur={handleSubtitleBlur}
            placeholder={t('photoType:subtitlePlaceholder')}
            maxLength={50}
            disabled={disabled}
            status={subtitleError ? 'error' : undefined}
          />
        </SubtitleRow>
      )}

      {hasError && (
        <ErrorRow>
          {[typeError, titleError, subtitleError]
            .filter((e): e is string => typeof e === 'string' && e.length > 0)
            .filter((e, i, arr) => arr.indexOf(e) === i)
            .map((error) => (
              <ErrorItem key={error}>
                <Icon iconSrc={ErrorCircleSvg} iconSize={16} />
                <ErrorText>{t(error)}</ErrorText>
              </ErrorItem>
            ))}
        </ErrorRow>
      )}
    </SlotWrapper>
  );
};

PhotoTypeSlotItemComponent.displayName = 'PhotoTypeSlotItem';

export const PhotoTypeSlotItem = memo(PhotoTypeSlotItemComponent);
export default PhotoTypeSlotItem;
